import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import type { StageId } from '@/types'

export interface UploadGuestAvatarInput {
  guestId: string
  teamId: string
  file: File
}

/**
 * Hook to upload a guest avatar to Supabase Storage
 * and update the guest record with the new URL
 */
export function useUploadGuestAvatar() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ guestId, teamId, file }: UploadGuestAvatarInput): Promise<string> => {
      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024 // 2MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 2MB')
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File must be a JPEG, PNG, GIF, or WebP image')
      }

      // Generate unique filename: {team_id}/{guest_id}/{timestamp}.{ext}
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      const path = `${teamId}/${guestId}/${Date.now()}.${ext}`

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('guest-avatars')
        .upload(path, file, {
          upsert: true,
          contentType: file.type,
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw new Error('Failed to upload image')
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('guest-avatars')
        .getPublicUrl(path)

      const publicUrl = urlData.publicUrl

      // Update guest record with new avatar URL
      const { error: updateError } = await supabase
        .from('guests')
        .update({ avatar_url: publicUrl } as never)
        .eq('id', guestId)

      if (updateError) {
        console.error('Update error:', updateError)
        throw new Error('Failed to update guest avatar')
      }

      return publicUrl
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      queryClient.invalidateQueries({ queryKey: ['guest'] })
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
    },
  })
}

/**
 * Hook to delete a guest avatar from storage
 */
export function useDeleteGuestAvatar() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ guestId, avatarUrl }: { guestId: string; avatarUrl: string }) => {
      // Extract path from URL
      const urlParts = avatarUrl.split('/guest-avatars/')
      if (urlParts.length !== 2) {
        throw new Error('Invalid avatar URL')
      }
      const path = urlParts[1]

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('guest-avatars')
        .remove([path])

      if (deleteError) {
        console.error('Delete error:', deleteError)
        // Don't throw - still update the guest record
      }

      // Clear avatar URL from guest record
      const { error: updateError } = await supabase
        .from('guests')
        .update({ avatar_url: null } as never)
        .eq('id', guestId)

      if (updateError) {
        throw new Error('Failed to update guest')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      queryClient.invalidateQueries({ queryKey: ['guest'] })
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
    },
  })
}

// Stages that indicate a guest has "accepted" and is a real guest
const acceptedStages: StageId[] = ['responded', 'prep', 'recorded', 'published']

interface GuestDeletability {
  canDelete: boolean
  reason?: string
}

interface AppearanceStage {
  id: string
  stage: string
}

/**
 * Hook to check if a guest can be permanently deleted.
 * A guest can only be deleted if they have no appearances beyond the 'invited' stage.
 * Once a guest has responded/accepted, they become a "real guest" and can only be archived.
 */
export function useCanDeleteGuest(guestId: string | undefined) {
  const supabase = createClient()

  return useQuery<GuestDeletability>({
    queryKey: ['guest-deletability', guestId],
    queryFn: async () => {
      if (!guestId) return { canDelete: false, reason: 'No guest ID' }

      // Check if guest has any appearances beyond 'invited' stage
      const { data: appearances, error } = await supabase
        .from('appearances')
        .select('id, stage')
        .eq('guest_id', guestId)

      if (error) {
        console.error('Error checking guest appearances:', error)
        return { canDelete: false, reason: 'Error checking appearances' }
      }

      // If guest has any appearance that has progressed beyond 'invited', they can't be deleted
      const hasAcceptedAppearance = (appearances as AppearanceStage[] | null)?.some(
        (a) => acceptedStages.includes(a.stage as StageId)
      )

      if (hasAcceptedAppearance) {
        return {
          canDelete: false,
          reason: 'Guest has accepted an invitation and cannot be deleted. You can archive them instead.',
        }
      }

      return { canDelete: true }
    },
    enabled: !!guestId,
  })
}

/**
 * Hook to permanently delete a guest.
 * This will also delete any 'invited' appearances and decrement the subscription counter.
 * Only use this for guests who have not accepted any invitations.
 */
export function useDeleteGuest() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (guestId: string): Promise<void> => {
      // SECURITY: Re-verify the guest can be deleted before proceeding
      // This prevents deletion of "real" guests even if the UI state is stale
      const { data: appearances, error: checkError } = await supabase
        .from('appearances')
        .select('id, stage')
        .eq('guest_id', guestId)

      if (checkError) {
        throw new Error('Failed to verify guest can be deleted')
      }

      const hasAcceptedAppearance = (appearances as AppearanceStage[] | null)?.some(
        (a) => acceptedStages.includes(a.stage as StageId)
      )

      if (hasAcceptedAppearance) {
        throw new Error('Cannot delete guest who has accepted an invitation')
      }

      // Delete any appearances (they should all be 'invited' at this point)
      const { error: appearanceError } = await supabase
        .from('appearances')
        .delete()
        .eq('guest_id', guestId)

      if (appearanceError) {
        console.error('Error deleting appearances:', appearanceError)
        // Continue with guest deletion even if appearance deletion fails
      }

      // Delete the guest
      const { error: deleteError } = await supabase
        .from('guests')
        .delete()
        .eq('id', guestId)

      if (deleteError) throw deleteError

      // Decrement the guests_used counter
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user) {
        const { error: rpcError } = await supabase.rpc(
          'decrement_guests_used' as never,
          { p_user_id: user.id } as never
        )

        if (rpcError) {
          // Log the error but don't fail the mutation since the guest is already deleted
          // The quota will be slightly off but the user can contact support
          console.error('Failed to decrement guest quota:', rpcError)
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      queryClient.invalidateQueries({ queryKey: ['guest'] })
      queryClient.invalidateQueries({ queryKey: ['guest-appearances'] })
      queryClient.invalidateQueries({ queryKey: ['guest-deletability'] })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  })
}
